/**
 * SystemLoadIndicator — the small honest live signal under the AgntSpace logo.
 *
 * Segmented thermometer: 7 cells in a horizontal row, each with a fixed color
 * along a yellow → red gradient. Lit-cell count reflects the level returned
 * by `GET /api/system/busy`:
 *
 *   - calm (idle):       0 lit, full thermometer dim
 *   - working (1-3 ops): 2 lit (yellow-amber), gentle pulse on the edge cell
 *   - busy (4+ ops):     4 lit (through orange), faster pulse
 *   - overloaded:        7 lit (full red), urgent pulse
 *   - disconnected:      0 lit, neutral gray (no pulse)
 *
 * Hover shows a tooltip listing what's running so the user knows WHY the
 * system is busy (e.g. "Ingesting 3 files in main · Editor quality pass").
 *
 * Polls every 5s. Endpoint is cached server-side at 2s, so multiple browser
 * tabs polling concurrently is cheap.
 */
import { useEffect, useRef, useState } from "react";
import styles from "./SystemLoadIndicator.module.css";

/**
 * Client-side fetch tracker. Instruments window.fetch ONCE at MODULE LOAD
 * (not in useEffect) so every API call from the UI is counted from the
 * very first render — even fetches that fire during initial mount before
 * Sidebar's useEffect has run. This is what makes the indicator react when
 * the dashboard fires 6+ endpoints concurrently on cold load.
 *
 * The bar reads this counter and shows "working" instantly when the UI
 * is waiting on a request — regardless of what the server thinks. This
 * is what makes the indicator react when the user clicks a link and the
 * page hangs for 5 seconds on `/api/wiki/stats`: the server isn't
 * dispatching anything, so its `active_dispatches` is 0, but the UI's
 * own fetch IS in flight.
 */
let activeFetches = 0;
const fetchSubscribers = new Set<(count: number) => void>();

// IMPORTANT: instrument at module-load time so we catch fetches that fire
// before any component mounts (initial dashboard load, route bootstrap, etc.).
// The check `typeof window` makes this safe in SSR contexts even though we
// don't use SSR — defensive.
if (typeof window !== "undefined" && !(window as { __agntFetchPatched?: boolean }).__agntFetchPatched) {
	(window as { __agntFetchPatched?: boolean }).__agntFetchPatched = true;
	const orig = window.fetch.bind(window);
	window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
		// Skip the busy endpoint itself — it's the polling that powers the
		// indicator, not real user-driven traffic.
		const url = typeof input === "string"
			? input
			: input instanceof URL
				? input.toString()
				: input.url;
		const isOurPoll = url.includes("/api/system/busy");
		if (!isOurPoll) {
			activeFetches++;
			fetchSubscribers.forEach((cb) => cb(activeFetches));
		}
		try {
			return await orig(input, init);
		} finally {
			if (!isOurPoll) {
				activeFetches = Math.max(0, activeFetches - 1);
				fetchSubscribers.forEach((cb) => cb(activeFetches));
			}
		}
	};
}

function useActiveFetches(): number {
	const [count, setCount] = useState(activeFetches);
	useEffect(() => {
		const cb = (c: number) => setCount(c);
		fetchSubscribers.add(cb);
		// Catch any fetches that started between module-load instrumentation
		// and this component's mount — sync the initial count.
		setCount(activeFetches);
		return () => {
			fetchSubscribers.delete(cb);
		};
	}, []);
	return count;
}

type LoadLevel = "calm" | "working" | "busy" | "overloaded" | "disconnected";

interface BusyOperation {
	kind: string;
	agent?: string;
	target?: string;
	elapsed_s: number;
}

interface BusyResponse {
	level: LoadLevel;
	summary: string;
	active_dispatches: number;
	queue: {
		pending: number;
		retrying: number;
		running_live: number;
		running_orphan: number;
		completed: number;
		dead_letter: number;
	};
	operations: BusyOperation[];
	decision_logger: {
		attempts: number;
		failures: number;
		rate: number;
		// `last_error` / `last_error_age_s` are sent by /api/system/busy but are
		// deliberately NOT declared or rendered here: this is a compact load
		// indicator, and a decision-log failure gets a real explanation on
		// /admin (Rule 26 — cause + remedies) where there is room for it.
		// Declaring them without rendering them made this file read as an
		// un-wired failure surface to the Rule 26 guard when it is not one.
	};
	recent_lock_errors: number;
	longest_op_seconds: number;
	// Phase 2 signals (added 2026-05-07) — optional in the type so old
	// servers without the wider surface keep rendering correctly.
	event_loop?: {
		blocks_last_minute: number;
		blocks_over_500ms_last_minute: number;
		blocks_over_5s_last_minute: number;
	};
	memory?: {
		action: string;
	};
	watcher?: {
		running: boolean;
		cycle_overrun: boolean;
		cycle_overrun_seconds: number;
	};
	cron?: {
		running_count: number;
	};
}

const POLL_INTERVAL_MS = 5000;

// Thermometer geometry — 7 cells in a yellow → red gradient.
const CELL_COUNT = 7;

// How many cells light up at each level. Calm/disconnected show no fill so
// the dimmed thermometer outline still reads as "i'm here, idle".
const LIT_COUNT_FOR_LEVEL: Record<LoadLevel, number> = {
	calm: 0,
	working: 2,
	busy: 4,
	overloaded: 7,
	disconnected: 0,
};

export function SystemLoadIndicator() {
	const [data, setData] = useState<BusyResponse | null>(null);
	const [serverLevel, setServerLevel] = useState<LoadLevel>("calm");
	const [hover, setHover] = useState(false);
	const abortRef = useRef<AbortController | null>(null);

	// Client-side: count of in-flight fetches RIGHT NOW. Updates instantly
	// the moment the user clicks a link, before the server has any signal.
	const inflight = useActiveFetches();

	useEffect(() => {
		let mounted = true;

		async function poll() {
			abortRef.current?.abort();
			const ctl = new AbortController();
			abortRef.current = ctl;
			try {
				const res = await fetch("/api/system/busy", {
					signal: ctl.signal,
				});
				if (!mounted) return;
				if (!res.ok) {
					setServerLevel("disconnected");
					return;
				}
				const body = (await res.json()) as BusyResponse;
				if (!mounted) return;
				setData(body);
				setServerLevel(body.level);
			} catch {
				if (!mounted) return;
				setServerLevel("disconnected");
			}
		}

		poll();
		const interval = setInterval(poll, POLL_INTERVAL_MS);
		return () => {
			mounted = false;
			clearInterval(interval);
			abortRef.current?.abort();
		};
	}, []);

	// Compose the visible level: the worse of (server-reported, client-inflight).
	// Client-inflight wins when ANY fetch is in flight — this is what makes the
	// bar react to slow `/api/wiki/stats` calls the server doesn't track.
	// Hierarchy: overloaded > busy > working > calm > disconnected
	let level: LoadLevel = serverLevel;
	if (serverLevel !== "disconnected") {
		// Map inflight count to a client-side level
		let clientLevel: LoadLevel = "calm";
		if (inflight >= 4) clientLevel = "busy";
		else if (inflight >= 1) clientLevel = "working";

		// Take the worse of the two
		const rank = (l: LoadLevel) =>
			l === "overloaded" ? 4
			: l === "busy" ? 3
			: l === "working" ? 2
			: l === "calm" ? 1
			: 0; // disconnected
		level = rank(clientLevel) > rank(serverLevel) ? clientLevel : serverLevel;
	}

	const tooltipText = buildTooltipText(level, data, inflight);
	const litCount = LIT_COUNT_FOR_LEVEL[level];
	const wrapperCls = `${styles.wrapper} ${styles[`level_${level}`]}`;

	return (
		<div
			className={wrapperCls}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			role="status"
			aria-label={`System load: ${level}`}
			aria-live="polite"
		>
			<div className={styles.bar} title={tooltipText} aria-hidden="true">
				{Array.from({ length: CELL_COUNT }).map((_, idx) => {
					const isLit = idx < litCount;
					const isEdge = isLit && idx === litCount - 1;
					const cellCls = [
						styles.cell,
						styles[`cell_${idx}`],
						isLit ? styles.cellLit : styles.cellDim,
						isEdge ? styles.cellEdge : "",
					]
						.filter(Boolean)
						.join(" ");
					return <span key={idx} className={cellCls} />;
				})}
			</div>
			{hover && level !== "calm" && (
				<div className={styles.tooltip}>
					<div className={styles.tooltipTitle}>
						{inflight > 0 && data?.summary === "Idle"
							? `${inflight} UI request${inflight > 1 ? "s" : ""} in flight`
							: data?.summary ?? "loading…"}
					</div>
					{inflight > 0 && (
						<div className={styles.tooltipFooter}>
							{inflight} browser request{inflight > 1 ? "s" : ""} pending
						</div>
					)}
					{data && data.operations.length > 0 && (
						<ul className={styles.tooltipList}>
							{data.operations.slice(0, 5).map((op, idx) => (
								<li key={`${op.agent ?? op.kind}-${idx}`}>
									<span className={styles.opAgent}>
										{op.agent ?? op.kind}
									</span>
									{op.elapsed_s > 0 && (
										<span className={styles.opElapsed}>
											{formatElapsed(op.elapsed_s)}
										</span>
									)}
								</li>
							))}
						</ul>
					)}
					{data && data.queue.pending + data.queue.retrying > 0 && (
						<div className={styles.tooltipFooter}>
							{data.queue.pending + data.queue.retrying} job(s) queued
						</div>
					)}
					{data?.cron && data.cron.running_count > 0 && (
						<div className={styles.tooltipFooter}>
							{data.cron.running_count} cron job(s) running
						</div>
					)}
					{data?.event_loop && data.event_loop.blocks_over_500ms_last_minute > 0 && (
						<div
							className={
								data.event_loop.blocks_over_5s_last_minute >= 1
									? styles.tooltipWarn
									: styles.tooltipFooter
							}
						>
							{data.event_loop.blocks_over_5s_last_minute >= 1 ? "⚠ " : ""}
							Event loop: {data.event_loop.blocks_over_500ms_last_minute} stall(s) ≥500ms
							{data.event_loop.blocks_over_5s_last_minute >= 1
								? ` · ${data.event_loop.blocks_over_5s_last_minute} severe (≥5s)`
								: ""}
						</div>
					)}
					{data?.memory &&
						data.memory.action !== "ok" &&
						data.memory.action !== "no_data" && (
						<div
							className={
								data.memory.action.includes("alarm") || data.memory.action.includes("ceiling")
									? styles.tooltipWarn
									: styles.tooltipFooter
							}
						>
							{data.memory.action.includes("ceiling")
								? "⚠ Memory at hard ceiling"
								: data.memory.action.includes("alarm")
									? "⚠ Memory pressure (alarm)"
									: "Memory pressure (warn)"}
						</div>
					)}
					{data?.watcher && data.watcher.cycle_overrun && (
						<div className={styles.tooltipFooter}>
							Watcher cycle overrunning ({Math.round(data.watcher.cycle_overrun_seconds)}s)
						</div>
					)}
					{data && data.recent_lock_errors > 0 && (
						<div className={styles.tooltipWarn}>
							⚠ Database contention — recent lock errors detected
						</div>
					)}
				</div>
			)}
		</div>
	);
}

function buildTooltipText(
	level: LoadLevel,
	data: BusyResponse | null,
	inflight: number,
): string {
	if (level === "disconnected") return "System status: disconnected from server";
	const cap = level.charAt(0).toUpperCase() + level.slice(1);
	const parts: string[] = [data?.summary ?? "loading…"];
	if (inflight > 0) {
		parts.push(`${inflight} request${inflight > 1 ? "s" : ""} in flight`);
	}
	return `${cap}: ${parts.join(" · ")}`;
}

function formatElapsed(seconds: number): string {
	if (seconds < 60) return `${Math.round(seconds)}s`;
	const m = Math.floor(seconds / 60);
	const s = Math.round(seconds % 60);
	return `${m}m ${s}s`;
}
